import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation files
import en from '@/locales/en.json';
import fr from '@/locales/fr.json';
import es from '@/locales/es.json';
import ar from '@/locales/ar.json';
import de from '@/locales/de.json';
import pt from '@/locales/pt.json';
import it from '@/locales/it.json';
import nl from '@/locales/nl.json';
import pl from '@/locales/pl.json';
import ru from '@/locales/ru.json';
import zh from '@/locales/zh.json';
import ja from '@/locales/ja.json';
import tr from '@/locales/tr.json';
import ko from '@/locales/ko.json';
import sv from '@/locales/sv.json';
import da from '@/locales/da.json';
import ro from '@/locales/ro.json';
import cs from '@/locales/cs.json';
import hu from '@/locales/hu.json';
import fi from '@/locales/fi.json';
import hi from '@/locales/hi.json';
import th from '@/locales/th.json';
import vi from '@/locales/vi.json';
import el from '@/locales/el.json';
import no from '@/locales/no.json';
import id from '@/locales/id.json';
import ms from '@/locales/ms.json';
import tl from '@/locales/tl.json';
import bn from '@/locales/bn.json';

// Supported languages (matches routes.ts)
export const SUPPORTED_LANGUAGES = ['en', 'fr', 'es', 'ar', 'de', 'it', 'pt', 'nl', 'pl', 'ru', 'zh', 'ja', 'ko', 'th', 'tr', 'vi', 'el', 'sv', 'da', 'fi', 'no', 'hu', 'cs', 'ro', 'id', 'ms', 'tl', 'bn', 'hi'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

export const RTL_LANGUAGES: SupportedLanguage[] = ['ar'];
export const isRTL = (lang: SupportedLanguage): boolean => RTL_LANGUAGES.includes(lang);

// Language metadata
export const languages: { code: SupportedLanguage; name: string; flag: string; dir: 'ltr' | 'rtl' }[] = [
  { code: 'en', name: 'English', flag: '🇬🇧', dir: 'ltr' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', dir: 'ltr' },
  { code: 'es', name: 'Español', flag: '🇪🇸', dir: 'ltr' },
  { code: 'ar', name: 'العربية', flag: '🇦🇪', dir: 'rtl' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', dir: 'ltr' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹', dir: 'ltr' },
  { code: 'pt', name: 'Português', flag: '🇧🇷', dir: 'ltr' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱', dir: 'ltr' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱', dir: 'ltr' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺', dir: 'ltr' },
  { code: 'zh', name: '中文', flag: '🇨🇳', dir: 'ltr' },
  { code: 'ja', name: '日本語', flag: '🇯🇵', dir: 'ltr' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷', dir: 'ltr' },
  { code: 'ko', name: '한국어', flag: '🇰🇷', dir: 'ltr' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪', dir: 'ltr' },
  { code: 'da', name: 'Dansk', flag: '🇩🇰', dir: 'ltr' },
  { code: 'ro', name: 'Română', flag: '🇷🇴', dir: 'ltr' },
  { code: 'cs', name: 'Čeština', flag: '🇨🇿', dir: 'ltr' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺', dir: 'ltr' },
  { code: 'fi', name: 'Suomi', flag: '🇫🇮', dir: 'ltr' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳', dir: 'ltr' },
  { code: 'th', name: 'ไทย', flag: '🇹🇭', dir: 'ltr' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳', dir: 'ltr' },
  { code: 'el', name: 'Ελληνικά', flag: '🇬🇷', dir: 'ltr' },
  { code: 'no', name: 'Norsk', flag: '🇳🇴', dir: 'ltr' },
  { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩', dir: 'ltr' },
  { code: 'ms', name: 'Bahasa Melayu', flag: '🇲🇾', dir: 'ltr' },
  { code: 'tl', name: 'Filipino', flag: '🇵🇭', dir: 'ltr' },
  { code: 'bn', name: 'বাংলা', flag: '🇧🇩', dir: 'ltr' }
];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      fr: { translation: fr },
      es: { translation: es },
      ar: { translation: ar },
      de: { translation: de },
      pt: { translation: pt },
      it: { translation: it },
      nl: { translation: nl },
      pl: { translation: pl },
      ru: { translation: ru },
      zh: { translation: zh },
      ja: { translation: ja },
      tr: { translation: tr },
      ko: { translation: ko },
      sv: { translation: sv },
      da: { translation: da },
      ro: { translation: ro },
      cs: { translation: cs },
      hu: { translation: hu },
      fi: { translation: fi },
      hi: { translation: hi },
      th: { translation: th },
      vi: { translation: vi },
      el: { translation: el },
      no: { translation: no },
      id: { translation: id },
      ms: { translation: ms },
      tl: { translation: tl },
      bn: { translation: bn },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['path', 'localStorage', 'navigator'],
      lookupFromPathIndex: 0,
      caches: ['localStorage'],
    },
  });

export default i18n;
